import { doc, getDoc, updateDoc, collection, query, where, getDocs, increment, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

export interface ReferralData {
  referralCode: string;
  referralCount: number;
  premiumReferralCount: number;
  freeMonthsEarned: number;
  freeMonthsUsed: number;
  referredBy?: string;
}

export const generateReferralCode = (uid: string): string => {
  return uid.substring(0, 8).toUpperCase();
};

export const findUserByReferralCode = async (code: string): Promise<string | null> => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('referralCode', '==', code.toUpperCase()));
    const snapshot = await getDocs(q);
    
    if (!snapshot.empty) {
      return snapshot.docs[0].id;
    }
    return null;
  } catch (error) {
    console.error('Erro ao buscar código de indicação:', error);
    return null;
  }
};

export const registerReferral = async (newUserId: string, referralCode: string): Promise<boolean> => {
  try {
    const referrerId = await findUserByReferralCode(referralCode);
    
    if (!referrerId) {
      return false;
    }

    await setDoc(doc(db, 'users', newUserId), {
      referredBy: referrerId,
      referredAt: new Date().toISOString(),
      referralCode: generateReferralCode(newUserId)
    }, { merge: true });

    const referrerRef = doc(db, 'users', referrerId);
    await updateDoc(referrerRef, {
      referralCount: increment(1)
    });

    const referrerDoc = await getDoc(referrerRef);
    const referrerData = referrerDoc.data();
    const newCount = (referrerData?.referralCount || 0);
    
    if (newCount % 5 === 0) {
      await updateDoc(referrerRef, {
        freeMonthsEarned: increment(1)
      });
    }

    return true;
  } catch (error) {
    console.error('Erro ao registrar indicação:', error);
    return false;
  }
};

export const registerPremiumReferral = async (userId: string): Promise<void> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    const userData = userDoc.data();
    
    if (!userData?.referredBy) {
      return;
    }

    const referrerId = userData.referredBy;
    const referrerRef = doc(db, 'users', referrerId);

    await updateDoc(referrerRef, {
      premiumReferralCount: increment(1)
    });

    const referrerDoc = await getDoc(referrerRef);
    const referrerData = referrerDoc.data();
    const newPremiumCount = (referrerData?.premiumReferralCount || 0);
    
    if (newPremiumCount % 2 === 0) {
      await updateDoc(referrerRef, {
        freeMonthsEarned: increment(1)
      });
    }
  } catch (error) {
    console.error('Erro ao registrar Premium referral:', error);
  }
};

export const activateFreeMonth = async (userId: string): Promise<boolean> => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data();
    
    if (!userData) {
      return false;
    }

    const freeMonthsEarned = userData.freeMonthsEarned || 0;
    const freeMonthsUsed = userData.freeMonthsUsed || 0;
    const available = freeMonthsEarned - freeMonthsUsed;
    
    if (available <= 0) {
      return false;
    }

    const date = new Date();
    date.setMonth(date.getMonth() + 1);

    await updateDoc(userRef, {
      freeMonthsUsed: increment(1),
      isPremium: true,
      subscriptionEndDate: date.toISOString(),
      subscriptionPlan: 'referral-free'
    });

    return true;
  } catch (error) {
    console.error('Erro ao usar mês grátis:', error);
    return false;
  }
};

export const checkFreeMonthsAvailable = async (userId: string): Promise<number> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    const userData = userDoc.data();
    
    if (!userData) {
      return 0;
    }

    const earned = userData.freeMonthsEarned || 0;
    const used = userData.freeMonthsUsed || 0;
    
    return Math.max(0, earned - used);
  } catch (error) {
    console.error('Erro ao verificar meses grátis:', error);
    return 0;
  }
};

export const getReferralStats = async (userId: string): Promise<ReferralData> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    const userData = userDoc.data();
    
    return {
      referralCode: userData?.referralCode || generateReferralCode(userId),
      referralCount: userData?.referralCount || 0,
      premiumReferralCount: userData?.premiumReferralCount || 0,
      freeMonthsEarned: userData?.freeMonthsEarned || 0,
      freeMonthsUsed: userData?.freeMonthsUsed || 0,
      referredBy: userData?.referredBy
    };
  } catch (error) {
    console.error('Erro ao obter stats de indicação:', error);
    return {
      referralCode: generateReferralCode(userId),
      referralCount: 0,
      premiumReferralCount: 0,
      freeMonthsEarned: 0,
      freeMonthsUsed: 0
    };
  }
};
